import { EventListener } from '..'
import UnhandledError from '../UnhandledError'

/** Shim for IE11 + Safari */
if (typeof window !== 'undefined') {
  window.requestIdleCallback =
    window.requestIdleCallback ||
    function (cb) {
      const start = Date.now()
      return setTimeout(function () {
        cb({
          didTimeout: false,
          timeRemaining: function () {
            return Math.max(0, 50 - (Date.now() - start))
          }
        })
      }, 1)
    }

  window.cancelIdleCallback =
    window.cancelIdleCallback ||
    function (id) {
      window.clearTimeout(id)
    }
}

interface PendingEvent<T> {
  handler: EventListener<T>
  data: T
  error?: UnhandledError
}

export class EventQueue {
  maxTimeout: number
  isHandlingEvents = false
  pendingEvents: PendingEvent<any>[] = []

  constructor(timeout = 2000) {
    this.maxTimeout = timeout
  }

  public push = <T>(
    handler: EventListener<T>,
    data: T,
    error?: UnhandledError
  ) => {
    this.pendingEvents.push({ handler, data, error })

    if (!this.isHandlingEvents) {
      this.handleWhenIdle()
    }
  }

  private handleWhenIdle = () => {
    window.requestIdleCallback(
      (deadline) => {
        this.isHandlingEvents = true

        let message: PendingEvent<any> | undefined

        try {
          while (
            (deadline.timeRemaining() > 0 || deadline.didTimeout) &&
            (message = this.pendingEvents.shift())
          ) {
            const { handler, data, error } = message

            handler(data, error)
          }
        } catch (error) {
          console.error(error)
        } finally {
          if (this.pendingEvents.length > 0) {
            setTimeout(() => {
              this.handleWhenIdle()
            }, 0)
            return
          }

          this.isHandlingEvents = false
        }
      },
      { timeout: this.maxTimeout }
    )
  }
}
